import { writable, derived } from "svelte/store";
import { sidePanel } from "./sidePanel";
import ReplyPanel from "$components/forum/ReplyPanel/ReplyPanel.svelte";
import api from "api";
import type { SemanticRelations, ThreadPost } from "@outverse-video/outverse-api-client-lib";
import type { Thread } from "app/models/forums";

interface SemanticLoading {
  semanticFetched: boolean;
}

type ReplyPanelType = ThreadPost & SemanticRelations & SemanticLoading & { replyThread: Thread };

const replyPanelStore = () => {
  const initialState: ReplyPanelType = {
    uuid: "",
    created: "",
    modified: "",
    author: {
      uuid: "",
      name: "",
      handle: "",
      profileColor: "",
      profileImage: "",
      roles: [],
    },
    isRemoved: false,
    content: [""],
    parentIsRemoved: "",
    replyToPostAuthor: {
      uuid: "",
      name: "",
      handle: "",
      profileColor: "",
      profileImage: "",
      roles: [],
      joinedCommunityAt: "",
    },
    reactions: [],
    upvoteCount: 0,
    userUpvoted: false,
    repliesCount: 0,
    seqNum: 0,
    thread: "",

    // SEMANTIC SECTION
    documents: [],
    discussions: [],
    semanticFetched: false,

    // THREAD DATA OF REPLY
    replyThread: {
      forumSpace: undefined,
      author: {
        uuid: "",
        handle: "",
        profileColor: "",
        profileImage: "",
        roles: [],
        name: "",
      },
      myPermissions: { userId: "", permissions: [] },
      repliers: [],
      uuid: "",
      title: "",
      content: {},
      created: "",
      solvable: false,
      solved: false,
      solution: {
        uuid: "",
        text: "",
        created: "",
        seqNum: "",
        isRemoved: false,
        author: {
          uuid: "",
          name: "",
          handle: "",
          profileColor: "",
          profileImage: "",
          joinedCommunityAt: "",
          roles: [],
        },
      },
      lastRepliedAt: "",
      replyCount: 0,
      deletedCount: 0,
      locked: false,
      pinned: false,
      hidden: false,
      maximized: false,
      isSubscribedToActivity: false,
      isSubscribedToReplies: false,
      directRepliesCount: 0,
      newRepliesCount: 0,
      upvoteCount: 0,
      favourite: false,
      userUpvoted: false,
      labels: [],
    },
  };

  const { subscribe, update } = writable<ReplyPanelType>({ ...initialState });
  return {
    subscribe,
    setActiveReply: async (reply: ThreadPost) => {
      update((state) => ({
        ...state,
        ...reply,
      }));
    },
    setActiveThread: async (activeThread: Thread) => {
      if (!activeThread) return;
      update((state) => ({
        ...state,
        replyThread: activeThread,
      }));
    },
    clearFetched: () => update((state) => ({ ...state, semanticFetched: false })),
    clearAndShow: async (replyUuid: string, _threadUuid: string, communitySlug: string) => {
      // clear state if uuid is different
      update((state) => {
        if (state.uuid !== replyUuid) {
          return { ...initialState };
        }
        return state;
      });

      sidePanel.setPanel(ReplyPanel, "Reply");
      sidePanel.show();

      // fetch semantic reply data
      try {
        const data = (
          await api.communitiesSemanticSearchGet({
            communitySlug: communitySlug,
            entityType: "REPLY",
            uuid: replyUuid,
          })
        ).data;

        update((state) => ({
          ...state,
          ...data,
        }));
      } catch (error) {
        console.warn(error);
      }
      update((state) => ({
        ...state,
        semanticFetched: true,
      }));
    },
    hide: () => sidePanel.hide(),
  };
};

export const replyPanel = replyPanelStore();
export const replyUuid = derived(replyPanel, ($replyPanel) => $replyPanel.uuid);
export const replyThread = derived(replyPanel, ($replyPanel) => $replyPanel.replyThread);
export const isSemanticDataFetched = derived(
  replyPanel,
  ($replyPanel) => $replyPanel.semanticFetched
);
export const semanticDocuments = derived(replyPanel, ($replyPanel) => $replyPanel.documents);
export const semanticDiscussions = derived(replyPanel, ($replyPanel) => $replyPanel.discussions);
export const semanticRelations = derived(replyPanel, ($replyPanel) => ({
  documents: $replyPanel.documents,
  discussions: $replyPanel.discussions,
}));
